import { z } from "zod"
import { WithNewCompanyIdSchema } from "../auth/response_data"

export const GenderEnum = {
  Male: "Male",
  Female: "Female",
  Other: "Other",
} as const

export const CustomerLogKindEnum = {
  AIDisabled: "AIDisabled",
  AIResumed: "AIResumed",
  FraudDetected: "FraudDetected",
  BrandRelatedFraudReported: "BrandRelatedFraudReported",
  TrollingDetected: "TrollingDetected",
} as const

export type CustomerLogKind = (typeof CustomerLogKindEnum)[keyof typeof CustomerLogKindEnum]

export const CustomerLogResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  kind: z.enum(CustomerLogKindEnum),
  source: z.string().nullable(),
  employee: z
    .object({
      id: z.bigint().transform(String),
      name: z.string(),
    })
    .nullable(),
  admin: z
    .object({
      id: z.bigint().transform(String),
      name: z.string(),
    })
    .nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export type Gender = (typeof GenderEnum)[keyof typeof GenderEnum]

export const CompanyClientResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  externalId: z.string(),
  name: z.string().nullable(),
})

export const CompanyClientsResponseDataSchema = z.array(CompanyClientResponseDataSchema)

const CustomerTagSourceEnum = {
  Permut: "Permut",
  Shopify: "Shopify",
} as const

const TagResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  name: z.string(),
})

export const TagsResponseDataSchema = z.array(TagResponseDataSchema)

export const CustomerTagsResponseDataSchema = z.array(
  TagResponseDataSchema.extend({
    source: z.enum(CustomerTagSourceEnum),
  }),
)

export const ShopifyCustomerResponseDataSchema = z.object({
  externalId: z.string(),
  ordersCount: z.number().nullable(),
  lastOrderAt: z
    .date()
    .transform((value) => value.toISOString())
    .nullable(),
  emailMarketingState: z.string().nullable(),
  smsMarketingState: z.string().nullable(),
  whatsappMarketingState: z.string().nullable(),
  rfmGroup: z.string().nullable(),
  note: z.string().nullable(),
  taxExempt: z.boolean(),
  taxExemptions: z.array(z.string()),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const CustomerResponseDataSchema = WithNewCompanyIdSchema.extend(
  z.object({
    id: z.bigint().transform(String),
    name: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    tentativeName: z.string().nullable(),
    tentativeFirstName: z.string().nullable(),
    tentativeLastName: z.string().nullable(),
    email: z.string().nullable(),
    tentativeEmail: z.string().nullable(),
    phone: z.string().nullable(),
    tentativePhone: z.string().nullable(),
    country: z.string().nullable(),
    locale: z.string().nullish(),
    preferredLocale: z.string().nullish(),
    metadata: z.union([
      z.record(z.string(), z.any()),
      z.null(),
      z.number(),
      z.boolean(),
      z.string(),
    ]),
    latestNps: z.number().optional().nullable(),
    totalResponses: z.number().optional().nullable(),
    totalTickets: z.number().optional().nullable(),
    messagesCount: z.number().default(0),
    lastMessageSentAt: z
      .date()
      .transform((value) => value?.toISOString() ?? undefined)
      .nullable()
      .optional(),
    isFake: z.boolean().default(false),
    useChannel: z.boolean().default(false),
    aiDisabled: z.boolean().default(false),
    optedOutAt: z.date().optional().nullable(),
    optedOutConversationId: z.bigint().optional().nullable(),
    denylistedAt: z.date().optional().nullable(),
    archivedAt: z
      .date()
      .transform((value) => value.toISOString())
      .optional()
      .nullable(),
    originalCustomerId: z.bigint().transform(String).optional().nullable(),
    createdAt: z.date().transform((value) => value.toISOString()),
    updatedAt: z.date().transform((value) => value.toISOString()),
    sentiment: z.number().optional().nullable(),
    instagramProfile: z
      .object({
        instagramId: z.string(),
        username: z.string().nullish(),
        name: z.string().nullish(),
        profilePicture: z.string().nullish(),
        followersCount: z.number().nullish(),
        isVerified: z.boolean().nullish(),
      })
      .nullish(),
    countryCode: z.string().optional(),
    client: CompanyClientResponseDataSchema.nullish(),
    tags: CustomerTagsResponseDataSchema.optional(),
    shopifyCustomer: ShopifyCustomerResponseDataSchema.nullish(),
  }).shape,
)

export const CustomersResponseDataSchema = z.array(CustomerResponseDataSchema)

export const CustomerLogsResponseDataSchema = z.array(CustomerLogResponseDataSchema)

export const GenerateFakeCustomerResponseDataSchema = z.object({
  customer: CustomerResponseDataSchema,
})

export const CustomersFunnelResponseDataSchema = z.object({
  currentPeriod: z.object({
    reachedCount: z.number(),
    openedCount: z.number(),
    engagedCount: z.number(),
    convertedCount: z.number(),
  }),
  previousPeriod: z.object({
    reachedCount: z.number(),
    openedCount: z.number(),
    engagedCount: z.number(),
    convertedCount: z.number(),
  }),
})

export const DisableCustomerAIByInstagramResponseDataSchema = z.object({
  accepted: z.literal(true),
})
